// The collaboration keys in the status bar, and its commands in the palette.
import { useCollab } from "../../collab/CollabProvider.tsx";
import { useProjectPeople } from "../../collab/hooks.ts";
import type { PaletteEntry } from "../Palette.tsx";

/**
 * Status-bar key hints for comments (and following). Nothing outside a project. In the folder list and
 * tree, letters jump to items (the keyboard model), so there C is not offered; the palette has "Comment here".
 */
export function CollabKeys({ list = false }: { list?: boolean }) {
  const c = useCollab();
  if (!c?.projectId) return null;
  const follow = c.following && (
    <>
      {" "}
      <kbd>esc</kbd> stop following
    </>
  );
  if (list) return follow || null;
  if (c.commentMode)
    return (
      <>
        {" "}
        <span className="pc-keys-mode" title="Esc leaves note mode">
          Note mode
        </span>{" "}
        click to pin · drag for a box
      </>
    );
  return (
    <>
      {" "}
      <kbd>C</kbd> note <kbd>]</kbd>
      <kbd>[</kbd> next note <kbd>⇧C</kbd> resolved
      {follow}
    </>
  );
}

/** Palette commands: Comment here, Open inbox, Show resolved, Follow … */
export function useCollabCommands(): PaletteEntry[] {
  const c = useCollab();
  const people = useProjectPeople();
  if (!c) return [];
  const out: PaletteEntry[] = [
    {
      id: "cmd:inbox",
      group: "Collaborate",
      title: "Open inbox",
      hint: c.unread ? `${c.unread} unread` : undefined,
      icon: "bell",
      run: () => c.setInboxOpen(true),
    },
  ];
  if (!c.projectId) return out;
  out.unshift({
    id: "cmd:comment",
    group: "Collaborate",
    title: c.commentMode ? "Leave note mode" : "Note here",
    keys: "C",
    icon: "comment",
    run: () => c.setCommentMode(!c.commentMode),
  });
  out.push({
    id: "cmd:resolved",
    group: "Collaborate",
    title: c.showResolved ? "Hide resolved notes" : "Show resolved notes",
    keys: "⇧C",
    icon: "check",
    run: () => c.setShowResolved(!c.showResolved),
  });
  if (c.following)
    out.push({
      id: "cmd:unfollow",
      group: "Collaborate",
      title: "Stop following",
      keys: "esc",
      icon: "close",
      run: () => c.follow(null),
    });
  for (const p of people)
    if (p.id !== c.following)
      out.push({
        id: `cmd:follow:${p.userId}`,
        group: "Collaborate",
        title: `Follow ${p.name}`,
        hint: p.presence?.where?.label,
        icon: "eye",
        run: () => c.follow(p.id),
      });
  return out;
}
